import React, { useEffect, useState } from 'react';
import CAppBreadcrumbs from '../../../app/components/CAppBreadcrumbs';
import CAppSubTitle from '../../../app/components/CAppSubTitle';
import CAppTitle from '../../../app/components/CAppTitle';
import AppCustomIcon from '../../../app/features/icon/AppIcon';
import HAppRoutes from '../../../app/features/routes/helpers/HAppRoutes';
import DocumentRoutes from '../DocumentRoutes';
import { useParams, useNavigate } from 'react-router-dom';
import useInvestExpertises from '../../investor/hooks/expertise/useInvestExpertises';
import useInvestFunds from '../../investor/hooks/fund/useInvestFunds';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Grid, Link, Stack, useMediaQuery, useTheme } from '@mui/material';
import CAppPaper from '../../../app/components/CAppPaper';
import AppLoaderPage from '../../../app/features/loader/page/AppLoaderPage';
import IFundType from '../../investor/interfaces/expertise/IFundType';
import useDocumentFiles from '../hooks/useDocumentFiles';
import InvestorRoutes from '../../investor/InvestorRoutes';
import langDocumentFiles from '../langs/langDocumentFiles';
import langDocumentExpertise from '../langs/langDocumentExpertise';
import { Helmet } from 'react-helmet';
import { useAuthContext } from '../../auth/contexts/AuthContext';
import styleVariables from '../../../app/styles/styleVariables';
import _ from 'lodash';
import investmentFundStatusLang from '../../investor/features/fund-type/langs/investmentFundStatusLang';

const PDocumentExpertise = () => {
	const authContext = useAuthContext();
	const routeParams = useParams();
	const investExpertiseTag = routeParams.investExpertiseTag;
	const { t } = useTranslation();
	const navigate = useNavigate();

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const { funds } = useInvestFunds(investExpertiseTag);
	const { documents } = useDocumentFiles();
	const investExpertises = useInvestExpertises();

	const [investExpertise, setInvestExpertise] = useState<IFundType | null>(null);
	useEffect(() => {
		if (investExpertises && investExpertiseTag) {
			setInvestExpertise(investExpertises.find((x) => x.tag === investExpertiseTag) ?? null);
		}
	}, [investExpertises, investExpertiseTag]);

	useEffect(() => {
		// If only one fund, redirect on the fund detail page
		if (investExpertiseTag && funds && funds.length === 1) {
			navigate(
				HAppRoutes.getPath(DocumentRoutes.documents_fund.tag, {
					investExpertiseTag,
					investFundId: funds[0].id.toString(),
				}),
			);
		}
	}, [funds, investExpertiseTag, navigate]);

	return (
		<>
			{funds && investExpertise && documents ? (
				<>
					<Helmet>
						<title>
							{authContext.authContext === 'employee' ? '[E] ' : ''} {t(langDocumentFiles.title.tag)} -
							{' ' + investExpertise.name + ' - '}
							{authContext.currentCompany ? authContext.currentCompany.name : ''}
						</title>
					</Helmet>
					<CAppBreadcrumbs
						items={[
							{
								label: t(langDocumentFiles.title.tag),
								location: HAppRoutes.getPath(DocumentRoutes.dashboard.tag),
							},

							{
								label: investExpertise.name,
							},
						]}
					/>
					<Stack
						direction={!isMobile ? 'row' : 'column'}
						alignItems={!isMobile ? 'flex-end' : 'stretch'}
						justifyContent="space-between"
					>
						<CAppTitle title={investExpertise.name} />
						<Link
							sx={{
								marginBottom: '14px',
								fontSize: '0.875rem',
								fontWeight: 600,
								textDecoration: 'none',
								'&:hover': {
									textDecoration: 'underline',
								},
							}}
							onClick={() =>
								navigate(
									HAppRoutes.getPath(InvestorRoutes.fund_type.tag, {
										investExpertiseTag: investExpertise.tag,
									}),
								)
							}
						>
							<Stack direction="row" alignItems="center" spacing={1}>
								<Box sx={{ textAlign: 'right', width: '100%' }}>
									{t(langDocumentExpertise.link_go_to_expertise_synthesis.tag)}
								</Box>
								<AppCustomIcon tag="arrow-right" sx={{ fontSize: '0.875rem' }} />
							</Stack>
						</Link>
					</Stack>
					<CAppSubTitle title={t(langDocumentFiles.funds.tag)} icon={<AppCustomIcon tag="expertises" />} />
					<Grid container spacing={3}>
						{_.orderBy(funds, ['isRealized', 'name'], ['asc', 'asc']).map((fund, key) => {
							return (
								<Grid key={key} item sm={3} xs={12}>
									<Link
										className="fund-item"
										id={'fund-item-' + fund.id.toString()}
										sx={{ textDecoration: 'none' }}
										onClick={() =>
											navigate(
												HAppRoutes.getPath(DocumentRoutes.documents_fund.tag, {
													investExpertiseTag,
													investFundId: fund.id.toString(),
												}),
											)
										}
									>
										<CAppPaper
											variant="outlined"
											sx={{
												padding: '15px',
												backgroundColor: fund.isRealized ? 'transparent' : '',
												borderColor: fund.isRealized ? styleVariables.greyLightColor : '',
												borderWidth: fund.isRealized ? '1px' : '',
												borderStyle: fund.isRealized ? 'solid' : '',
												position: 'relative',
											}}
										>
											<Grid container spacing={2}>
												<Grid item sm={10}>
													<Box
														sx={{
															height: '70px',
															overflow: 'hidden',
															fontSize: '1.375rem',
															fontWeight: 600,
															lineHeight: 1.5,
														}}
													>
														{fund.name}
													</Box>
													<Stack
														direction="row"
														spacing={1}
														alignItems="center"
														justifyContent="left"
														sx={{
															fontSize: '0.875rem',
														}}
													>
														<AppCustomIcon
															tag="folders"
															color="secondary"
															sx={{ fontSize: '0.875rem' }}
														/>{' '}
														<Box>
															{t(langDocumentFiles.x_documents.tag, {
																count: documents?.filter((x) => x.fund?.id === fund.id)
																	.length,
															})}
														</Box>
													</Stack>
												</Grid>
												<Grid item sm={2}>
													<AppCustomIcon tag="arrow-right" />
												</Grid>
											</Grid>
											{fund.isRealized ? (
												<Chip
													variant="filled"
													label={t(investmentFundStatusLang.liquidated.tag)}
													size="small"
													sx={{
														position: 'absolute',
														bottom: '15px',
														right: '20px',
														backgroundColor: '#E3E8F0',
													}}
												/>
											) : (
												<></>
											)}
										</CAppPaper>
									</Link>
								</Grid>
							);
						})}
					</Grid>
				</>
			) : (
				<AppLoaderPage />
			)}
		</>
	);
};

export default PDocumentExpertise;
